import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { Limites } from "../Pdv/LImites";
import { Navigate, useParams } from "react-router-dom";
import { usePedido } from "../../hooks/pagamento/usePedido";
import { Loading } from "../../components/Loading";
import { formatBRL } from "../../utils/currency";
import { consultarPagamentoPos } from "../../hooks/pagamento/useConsultarPagamentoPos";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import Swal from "sweetalert2";
import { api } from "../../libs/axios";
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel } from "../../components/CheckoutProgress";
import { createCheckoutProgressOverride } from "../../utils/checkoutProgress";
import { useVisualCheckoutProgress } from "../../hooks/pagamento/useVisualCheckoutProgress";
import { CheckoutProgress, PedidoStatusCode } from "../../utils/interfaces";

export function PagamentoPos() {
  const { id } = useParams() as { id: string };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    isRestoringPedido,
    isPedidoPageVisible,
    pedidoValidationFailed,
    retryPedidoValidation,
  } = usePedido({ id, revalidateOnHistoryRestore: true });
  const pedido = data?.pedido;
  const hasValidPedido = pedido !== undefined && pedido !== null;
  const pedidoDisponivelParaPagamento = hasValidPedido && [
    PedidoStatusCode.Aberto,
    PedidoStatusCode.Reservado,
  ].includes(pedido.status);
  const [isCanceling, setIsCanceling] = useState(false);
  const [paymentDetected, setPaymentDetected] = useState(false);
  const checkoutEffectsAllowed =
    !isLoading &&
    !isFetching &&
    !isError &&
    !isRestoringPedido &&
    isPedidoPageVisible &&
    !pedidoValidationFailed &&
    pedidoDisponivelParaPagamento;
  const { data: checkoutProgress, refetch: refetchCheckoutProgress } = useCheckoutStatus(id, pedidoDisponivelParaPagamento && checkoutEffectsAllowed);

  const verificarPagamento = useCallback(async () => {
    if (!pedidoDisponivelParaPagamento || !checkoutEffectsAllowed) {
      return;
    }

    try {
      await consultarPagamentoPos(id);
      setPaymentDetected(true);
      await refetchCheckoutProgress();
    } catch (error) {
      const err = error as AxiosErrorResponse;

      if (err.response?.status === 404) {
        return;
      }

      const message =
        err.response?.data?.data?.message ??
        err.response?.data?.message;

      if (message) {
        Swal.fire({
          icon: "error",
          title: "Erro no pagamento",
          text: message,
        });
      }
    }
  }, [id, pedidoDisponivelParaPagamento, checkoutEffectsAllowed, refetchCheckoutProgress]);

  useEffect(() => {
    if (!pedidoDisponivelParaPagamento || !checkoutEffectsAllowed) {
      return;
    }

    const interval = setInterval(verificarPagamento, 30000);

    verificarPagamento();

    return () => clearInterval(interval);
  }, [pedidoDisponivelParaPagamento, checkoutEffectsAllowed, verificarPagamento]);

  const idleProgress = createCheckoutProgressOverride(
    id,
    "pos",
    "waiting_payment",
    "Aguardando pagamento",
    "Siga as instruções da máquina POS da WebPag e após o pagamento aguarde a confirmação automática do pedido.",
  );

  const handleCompleted = useCallback((progress: CheckoutProgress) => {
    const target = progress.redirect_to || `/finalizada/${id}`;
    window.location.assign(target);
  }, [id]);

  const effectiveProgress = useVisualCheckoutProgress({
    pedidoId: id,
    method: "pos",
    remoteProgress: checkoutProgress,
    idleProgress,
    enabled: checkoutEffectsAllowed,
    onCompleted: handleCompleted,
  }) ?? idleProgress;

  if (isLoading || isRestoringPedido || (!hasValidPedido && isFetching)) {
    return <Loading />;
  }

  if (isError || pedidoValidationFailed || !data || !pedido) {
    return (
      <Flex minH="60vh" alignItems="center" justifyContent="center">
        <Box bg="white" borderWidth={1} borderColor="gray.200" p={6} maxW="420px" textAlign="center">
          <Text fontSize="lg" fontWeight="bold" mb={2}>
            Não foi possível atualizar o status do pedido.
          </Text>
          <Text color="gray.600" mb={4}>
            Verifique sua conexão e tente novamente antes de continuar.
          </Text>
          <Button colorScheme="blue" onClick={() => void retryPedidoValidation()}>
            Tentar novamente
          </Button>
        </Box>
      </Flex>
    );
  }

  if (pedido.status === PedidoStatusCode.Finalizado) {
    return <Navigate to={`/finalizada/${pedido.id}`} replace />;
  }

  if (!pedidoDisponivelParaPagamento) {
    return <Navigate to="/" replace />;
  }

  const redirecionarSePedidoFinalizado = async () => {
    try {
      await consultarPagamentoPos(id);
      setPaymentDetected(true);
      await refetchCheckoutProgress();
      return true;
    } catch {
      return false;
    }
  };

  const cancelarPedido = async () => {
    try {
      setIsCanceling(true);

      if (await redirecionarSePedidoFinalizado()) {
        return;
      }

      await api.delete(`/pedido/${id}/cancelar`);

      await Swal.fire({
        icon: "success",
        title: "Pedido cancelado",
        text: "O pedido e os bilhetes foram cancelados.",
        confirmButtonText: "OK",
      });

      window.location.href = "/";
    } catch (error) {
      if (await redirecionarSePedidoFinalizado()) {
        return;
      }

      const err = error as AxiosErrorResponse;
      const message =
        err.response?.data?.data?.message ??
        err.response?.data?.message ??
        "Não foi possível cancelar o pedido.";

      Swal.fire({
        icon: "error",
        title: "Erro ao cancelar pedido",
        text: message,
      });
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <Box display={"flex"} flexDir={"column"} justifyContent={"center"} width={"full"} alignItems={"center"}>
      <Flex maxWidth={"140rem"} width={"100%"} flexDir={"column"} justifyContent={"center"}>
        <Header />
        <Box px={"1rem"} bg={"white"} w={"full"} display={"flex"} flexDir={"column"} gap={"8"} pb={8}>
          <Limites />
          <Box bg={"#f4f4f4"} p={6}>
            <Heading size={"xl"} mb={6} color={"blue.800"} fontWeight={"extrabold"}>
              Pagamento por POS
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={6}>
              <Stat>
                <StatLabel>Pedido</StatLabel>
                <StatNumber>{pedido.id}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Valor total</StatLabel>
                <StatNumber>{formatBRL(Number(pedido.valor_total))}</StatNumber>
              </Stat>
            </SimpleGrid>

            <Text mb={6} fontSize={"lg"} fontWeight={"medium"}>
              Siga as instruções da máquina POS da WebPag e após o pagamento aguarde a confirmação automática do pedido
            </Text>

            <Box mb={6}>
              <CheckoutProgressPanel
                progress={effectiveProgress}
                fallbackStep={paymentDetected ? "payment_registered" : "waiting_payment"}
                method="pos"
              />
            </Box>

            <Flex gap={4} flexWrap={"wrap"} alignItems={"center"}>
              <Button colorScheme={"red"} onClick={cancelarPedido} isLoading={isCanceling}>
                Cancelar Pedido
              </Button>
            </Flex>
          </Box>
        </Box>
        <Footer />
      </Flex>
    </Box>
  );
}
